// App shell — wraps every route with the header, Nav, and MobileNav.
// Applies the user's saved theme on mount and whenever opts.theme changes.

import { useEffect, useState, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import Nav from './Nav'
import MobileNav from './MobileNav'
import AlertBell from './AlertBell'
import SearchBar, { type SearchBarHandle } from './SearchBar'
import WhatsNewMenu from './WhatsNewMenu'
import BugReportModal from './BugReportModal'
import Walkthrough from './Walkthrough'
import { useOpts } from '../hooks/useOpts'
import { useAppState } from '../hooks/useAppState'
import { useAuth } from '../hooks/useAuth'
import { applyTheme, getStoredTheme } from '../lib/theme'
import type { ThemeKey } from '../lib/theme'
import { env } from '../env'
import { signOut } from '../lib/firebase/auth'
import { useUndoRedo } from '../lib/undoStore'
import { useAssignmentsRealtime } from '../hooks/useAssignmentsRealtime'
import { useAppStateRealtime } from '../hooks/useAppStateRealtime'
import { usePortalResponses } from '../hooks/usePortalResponses'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { useIncomingEstimateAlerts } from '../hooks/useIncomingEstimateAlerts'
import { useOutgoingEstimateAlerts } from '../hooks/useOutgoingEstimateAlerts'
import { usePortalLinkCOApproval } from '../hooks/usePortalLinkCOApproval'
import { usePortalLinkEstimateAccept } from '../hooks/usePortalLinkEstimateAccept'
import { useAssignmentParticipantHeal } from '../hooks/useAssignmentParticipantHeal'
import { useCleanupOrphanedReceivedProjects } from '../hooks/useCleanupOrphanedReceivedProjects'
import { useCleanupOrphanedReceivedEstimates } from '../hooks/useCleanupOrphanedReceivedEstimates'
import { useHealClientLinkedUids } from '../hooks/useHealClientLinkedUids'
import { useHealMissingPortalLinks } from '../hooks/useHealMissingPortalLinks'
import { exportJSON, importJSON } from '../lib/exportImport'
import { toastOk, toastErr, toastInfo } from '../lib/toastStore'
import { useQueryClient } from '@tanstack/react-query'
import { qk } from '../hooks/queryKeys'
import { saveAppState } from '../lib/db'
import type { AppState } from '../types/firestore'

// Apply the stored theme immediately (before first paint) to avoid flash.
const stored = getStoredTheme()
if (stored) applyTheme(stored)

function Clock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
  const date = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  return (
    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', lineHeight: 1.35, userSelect: 'none' }}>
      <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 12 }}>{time}</div>
      <div>{date}</div>
    </div>
  )
}

export default function Shell() {
  const opts = useOpts()
  const { uid } = useAuth()
  const qc = useQueryClient()
  const { undo, redo, canUndo, canRedo } = useUndoRedo(uid)
  const { alerts, ackAlert } = useAssignmentsRealtime()
  usePortalResponses()
  useIncomingEstimateAlerts()
  useOutgoingEstimateAlerts()
  usePortalLinkCOApproval()
  // Task 8: watch portalEstimate.portalStatus === 'accepted' on schema-v2
  // portallink__ docs. Complementary to usePortalResponses (which reads the
  // legacy top-level `status` field); the two hooks cover different schemas.
  usePortalLinkEstimateAccept()
  useAssignmentParticipantHeal()
  useCleanupOrphanedReceivedProjects()
  useCleanupOrphanedReceivedEstimates()
  // Back-fill linkedUid on Client/Sub rows created before the link-approval
  // fix. Runs BEFORE useHealMissingPortalLinks so that when the portal heal
  // reads client.linkedUid it finds the backfilled value.
  useHealClientLinkedUids()
  // V1 parity: every GC-owned project should have a portallink__ doc so
  // the receiver can resolve the portal URL without asking the sender.
  // New projects auto-create one via ensurePortalLinkForProject; this hook
  // back-fills pre-existing projects that were created before the auto flow.
  useHealMissingPortalLinks()
  // Realtime Firestore onSnapshot listeners for per-slice appstate docs.
  // Keeps owner + partner tabs in sync within ~1s without flash. See
  // src/hooks/useAppStateRealtime.ts for the echo-guard and merge logic.
  useAppStateRealtime()
  const loadRef = useRef<HTMLInputElement>(null)
  const searchRef = useRef<SearchBarHandle>(null)
  const [bugOpen, setBugOpen] = useState(false)
  const [walkOpen, setWalkOpen] = useState(false)

  // Online/offline toasts — V1 parity (public/app/index.html L5587–5592)
  const { online, hasChanged } = useOnlineStatus()
  useEffect(() => {
    if (!hasChanged) return
    if (online) toastOk('Back online')
    else toastInfo('Offline — changes will sync when reconnected')
  }, [online, hasChanged])

  // Cross-device freshness: when the tab becomes visible again (user
  // switched from phone → desktop or tabbed back in), proactively
  // invalidate the appstate so the next render pulls the latest writes
  // made on the other device. refetchOnWindowFocus on the QueryClient
  // covers most cases, but visibilitychange catches tabs that were
  // parked for a long time on mobile (where focus events can be flaky).
  useEffect(() => {
    if (!uid) return
    function onVisible() {
      if (document.visibilityState === 'visible') {
        void qc.invalidateQueries({ queryKey: qk.appstate(uid!) })
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [uid, qc])

  useEffect(() => {
    const key = opts.theme as ThemeKey | undefined
    if (key) applyTheme(key)
  }, [opts.theme])

  // Auto-trigger walkthrough on first load (or after Options → Replay tour,
  // which clears walkthroughSeenAt). 400ms delay lets the app paint first.
  // Gate on `appStateLoaded` — if we fire while appstate is still
  // mid-fetch, `opts` is DEFAULT_OPTS (no walkthroughSeenAt) and the
  // walkthrough auto-opens for every login regardless of whether the user
  // already completed it.
  const { data: appStateData } = useAppState()
  const appStateLoaded = !!appStateData
  const seen = (opts as Record<string, unknown>).walkthroughSeenAt as string | undefined
  useEffect(() => {
    if (!uid) return
    if (!appStateLoaded) return
    if (seen) return
    if (walkOpen) return
    const t = setTimeout(() => setWalkOpen(true), 400)
    return () => clearTimeout(t)
  }, [uid, appStateLoaded, seen, walkOpen])

  // Keyboard shortcuts: V1 parity (public/app/index.html L7042–7052).
  //   Ctrl/Cmd+Z         → undo
  //   Ctrl/Cmd+Shift+Z   → redo
  //   Ctrl/Cmd+Y         → redo
  //   Ctrl/Cmd+K or "/"  → focus global search
  //   Ctrl/Cmd+S         → preventDefault + "Auto-saved" toast (we autosave)
  //   Escape             → modals handle individually; no global handler.
  useEffect(() => {
    function isTypingTarget(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (el.isContentEditable) return true
      return false
    }

    function handler(e: KeyboardEvent) {
      const ctrl = e.ctrlKey || e.metaKey

      // "/" → focus search (only when not already typing in a field)
      if (!ctrl && e.key === '/' && !isTypingTarget(e.target)) {
        e.preventDefault()
        searchRef.current?.focus()
        return
      }

      if (!ctrl) return

      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault()
        searchRef.current?.focus()
        return
      }
      if (e.key === 's' || e.key === 'S') {
        // V1 autosaves; show a confirmation toast instead of letting the
        // browser's Save Page As dialog open.
        e.preventDefault()
        toastOk('Auto-saved')
        return
      }
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); void undo() }
      if ((e.key === 'Z' && e.shiftKey) || (e.key === 'y')) { e.preventDefault(); void redo() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [undo, redo])

  const bizName = (opts.bizName as string | undefined) || 'JobSite Pulse'

  function handleSave() {
    if (!uid) return
    const state = qc.getQueryData<AppState>(qk.appstate(uid))
    if (!state) return
    exportJSON(state)
  }

  function handleDownload() {
    if (!uid) return
    const state = qc.getQueryData<AppState>(qk.appstate(uid))
    if (!state) return
    exportJSON(state, 'FULL_BACKUP')
  }

  async function handleLoad(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !uid) return
    if (!confirm('Load data from this file? This will overwrite your current data.')) {
      e.target.value = ''
      return
    }
    try {
      const imported = await importJSON(file)
      qc.setQueryData(qk.appstate(uid), imported)
      await saveAppState(uid, imported)
      toastOk('Data loaded successfully')
    } catch (err) {
      toastErr(`Failed to load: ${String(err)}`)
    }
    e.target.value = ''
  }

  return (
    <>
      <div className="app">
        {/* Desktop header */}
        <header className="app-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="app-title">{bizName}</span>
            {env.isDev && (
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 10,
                background: 'var(--accent-light)', color: 'var(--accent-text)',
                padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                border: '1px solid rgba(62,168,160,0.2)',
              }}>
                v2 beta
              </span>
            )}
          </div>

          {/* Global search */}
          <SearchBar ref={searchRef} />

          <div className="app-header-right">
            <AlertBell />
            <WhatsNewMenu />
            <button className="btn-outline btn-sm" onClick={handleSave} title="Save data as JSON">⇓ Save</button>
            <label className="btn-outline btn-sm" style={{ cursor: 'pointer' }} title="Load data from JSON">
              ⇑ Load
              <input ref={loadRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleLoad} />
            </label>
            <button className="btn-primary btn-sm" onClick={handleDownload} title="Download full backup">↓ Download</button>
            <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </header>

        {/* Undo / redo + clock bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              className="btn-ghost btn-sm"
              onClick={() => void undo()}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
              style={{ fontSize: 13, padding: '3px 8px', minWidth: 0, opacity: canUndo ? 1 : 0.3 }}
            >
              ↩ Undo
            </button>
            <button
              className="btn-ghost btn-sm"
              onClick={() => void redo()}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
              style={{ fontSize: 13, padding: '3px 8px', minWidth: 0, opacity: canRedo ? 1 : 0.3 }}
            >
              Redo ↪
            </button>
          </div>
          <Clock />
        </div>

        {/* Desktop nav */}
        <Nav />

        {/* Assignment alerts banner */}
        {alerts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {alerts.map((a) => (
              <div key={`${a.assignId}-${a.kind}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 'var(--radius)',
                  background: 'var(--accent-light)', color: 'var(--accent-text)',
                  border: '1px solid rgba(62,168,160,0.3)',
                  fontSize: 13, fontWeight: 500,
                }}>
                <span style={{ fontSize: 16 }}>📬</span>
                <span style={{ flex: 1 }}>
                  <strong>{a.projectName}</strong> — {a.count} new contractor {a.kind}{a.count === 1 ? '' : 's'}
                </span>
                <button className="btn-ghost btn-sm" onClick={() => ackAlert(a.assignId)}
                  style={{ padding: '2px 10px' }}>Dismiss</button>
              </div>
            ))}
          </div>
        )}

        {/* Route content */}
        <main>
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav — fixed, outside .app */}
      <MobileNav />

      {/* Floating bug-report pill — opens report modal */}
      <button
        className="bug-pill"
        title="Report an issue"
        aria-label="Report an issue"
        onClick={() => setBugOpen(true)}
      >
        🐛
      </button>

      <BugReportModal open={bugOpen} onClose={() => setBugOpen(false)} />

      {/* Onboarding walkthrough — auto-opens on first load; Options→Replay resets
          walkthroughSeenAt which re-arms the auto-trigger effect above.
          Mount conditionally so each open gets fresh step state. */}
      {walkOpen && <Walkthrough open onClose={() => setWalkOpen(false)} />}
    </>
  )
}
